import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Clock, LayoutDashboard, Users, Briefcase, Bell, Settings, LogOut, ChevronRight, Menu, X, Shield,
  DollarSign, Calendar, Award, FolderLock, BarChart3, MessageSquare, UserPlus, Radio, Timer,
  Target, MessagesSquare, ScrollText
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { UserAvatar } from '../components/UserAvatar';

const buildSections = (user) => {
  const p = user?.permissions || {};
  const role = user?.role;
  const isMgmt = role === 'manager' || role === 'admin';

  if (role === 'client') {
    return [{
      title: 'Client Portal',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: Briefcase, label: 'Client Bridge', path: '/client-bridge' },
        { icon: BarChart3, label: 'SLA Progress', path: '/sla' },
        { icon: Settings, label: 'Settings', path: '/settings' },
      ],
    }];
  }

  const sections = [
    {
      title: 'My Workspace',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: Clock, label: 'Cadence Clock', path: '/time' },
        { icon: Timer, label: 'Hours Submission', path: '/hours' },
        { icon: DollarSign, label: 'Payroll', path: '/payroll' },
        { icon: Calendar, label: 'HR Suite', path: '/hr' },
        { icon: FolderLock, label: 'Documents', path: '/documents' },
        { icon: MessagesSquare, label: 'Messages', path: '/messages' },
        { icon: Bell, label: 'Notifications', path: '/notifications' },
      ],
    },
    {
      title: 'Performance',
      items: [
        { icon: Target, label: 'Performance Board', path: '/performance' },
        { icon: Award, label: 'Leaderboard', path: '/leaderboard' },
        { icon: Briefcase, label: 'Client Bridge', path: '/client-bridge' },
      ],
    },
  ];

  const mgmt = [];
  if (p.can_view_online_status) mgmt.push({ icon: Radio, label: 'Online Status', path: '/online-status' });
  if (isMgmt) mgmt.push({ icon: Users, label: 'Team Management', path: '/manager' });
  if (p.can_add_users || p.can_reset_passwords) mgmt.push({ icon: UserPlus, label: 'Staff Management', path: '/staff-management' });
  if (p.can_view_all_hours) mgmt.push({ icon: ScrollText, label: 'Hours Audit', path: '/hours?view=audit' });
  if (isMgmt) mgmt.push({ icon: MessageSquare, label: 'Client Inquiries', path: '/inquiries' });
  if (isMgmt) mgmt.push({ icon: BarChart3, label: 'Reports', path: '/reports' });
  if (mgmt.length) sections.push({ title: 'Management', items: mgmt });

  const admin = [];
  if (p.can_calibrate_targets) admin.push({ icon: Target, label: 'Targets & Recognition', path: '/performance?manage=1' });
  if (role === 'admin') admin.push({ icon: Shield, label: 'Admin Console', path: '/admin' });
  admin.push({ icon: Settings, label: 'My Profile', path: '/settings' });
  sections.push({ title: 'Administration', items: admin });

  return sections;
};

export const DashboardLayout = ({ children }) => {
  const { user, logout, api, avatarVersion } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const sections = buildSections(user);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications?unread_only=true');
      setNotifications(response.data.slice(0, 5));
      setUnreadCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const beat = () => api.post('/presence/heartbeat').catch(() => {});
    beat();
    const interval = setInterval(beat, 60000);
    return () => clearInterval(interval);
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    const [base, query] = path.split('?');
    if (query) return location.pathname === base && location.search.includes(query);
    if (base === '/dashboard') return location.pathname === base;
    return location.pathname === base && !location.search;
  };

  const currentLabel = location.pathname.split('/')[1]?.replace(/-/g, ' ') || 'dashboard';

  return (
    <div className="min-h-screen bg-[#0A091E]" data-testid="dashboard-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 h-full w-64 z-50 sidebar flex flex-col transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="h-16 shrink-0 flex items-center justify-between px-6 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0A091E]" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              SRM <span className="text-[#D4AF37]">Cadence</span>
            </span>
          </Link>
          <button className="lg:hidden text-white/50 hover:text-white" onClick={() => setSidebarOpen(false)} data-testid="btn-close-sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-5 sidebar-scroll" data-testid="sidebar-nav">
          {sections.map((section) => (
            <div key={section.title}>
              <p className="px-4 mb-2 text-[10px] uppercase tracking-[0.2em] text-white/30 font-semibold">{section.title}</p>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-link text-sm ${isActive(item.path) ? 'active' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    {item.label === 'Notifications' && unreadCount > 0 && (
                      <Badge className="ml-auto bg-[#D4AF37] text-[#0A091E] text-xs">{unreadCount}</Badge>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="shrink-0 p-4 border-t border-white/10 bg-[#0A091E]/60">
          <div className="flex items-center gap-3">
            <UserAvatar user={user} version={avatarVersion} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-[11px] text-white/50 truncate" title={user?.title || user?.role} data-testid="sidebar-user-title">
                {user?.title || user?.role}
              </p>
            </div>
            <button onClick={handleLogout} className="text-white/50 hover:text-[#D4AF37]" data-testid="btn-logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="h-16 bg-[#0A091E]/80 backdrop-blur-md border-b border-white/10 fixed top-0 right-0 left-0 lg:left-64 z-30">
          <div className="h-full flex items-center justify-between px-6">
            <button className="lg:hidden text-white/70 hover:text-white" onClick={() => setSidebarOpen(true)} data-testid="btn-mobile-menu">
              <Menu className="w-6 h-6" />
            </button>

            <div className="hidden lg:flex items-center gap-2 text-sm">
              <span className="text-white/50">Dashboard</span>
              {location.pathname !== '/dashboard' && (
                <>
                  <ChevronRight className="w-4 h-4 text-white/30" />
                  <span className="text-white capitalize">{currentLabel}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-4">
              {user?.permissions?.access_level_label && (
                <Badge className="hidden md:inline-flex bg-[#4B167A]/40 text-[#D4AF37] border border-[#D4AF37]/30" data-testid="header-access-level">
                  {user.permissions.access_level_label}
                </Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative text-white/70 hover:text-white" data-testid="btn-notifications">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#D4AF37] text-[#0A091E] text-xs flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80 bg-[#1F1B71] border-white/10 text-white">
                  <div className="p-3 border-b border-white/10"><h4 className="font-semibold">Notifications</h4></div>
                  {notifications.length > 0 ? notifications.map((notif) => (
                    <DropdownMenuItem key={notif.id} className="flex flex-col items-start p-3 cursor-pointer hover:bg-white/5">
                      <span className="font-medium">{notif.title}</span>
                      <span className="text-sm text-white/50 line-clamp-1">{notif.message}</span>
                    </DropdownMenuItem>
                  )) : (
                    <div className="p-4 text-center text-white/50">No new notifications</div>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem asChild>
                    <Link to="/notifications" className="w-full text-center text-[#D4AF37] hover:text-[#D4AF37]/80">View All Notifications</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2" data-testid="btn-user-menu">
                    <UserAvatar user={user} size="w-8 h-8" textClass="text-xs" version={avatarVersion} />
                    <span className="hidden md:block text-sm text-white">{user?.full_name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-[#1F1B71] border-white/10 text-white">
                  <div className="p-3 border-b border-white/10">
                    <p className="font-medium">{user?.full_name}</p>
                    <p className="text-sm text-white/50">{user?.email}</p>
                    <p className="text-xs text-[#D4AF37] mt-1">{user?.title}</p>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer hover:bg-white/5" data-testid="menu-profile">
                      <Settings className="w-4 h-4 mr-2" /> My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:bg-white/5 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="pt-16 min-h-screen">
          <div className="p-6 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
};
